import {TabSelectionType, WebTabItemProps, WebTabProps} from "mmcore-ui";
import {makeClassVariance, mergeWind} from "mfront-default-ui";
import {
    Tabs as TabPrimitive,
    List as TabListPrimitive,
    Trigger as TabTriggerPrimitive,
    Content as TabContentPrimitive,
} from "@radix-ui/react-tabs"
import {UIComponentProps} from "mmcore";


export function DefaultTab({className, items, defaultTabId, selectType = "line", orientation = "horizontal", ...props}: WebTabProps) {
    return (
        <Tabs defaultValue={defaultTabId} orientation={orientation} className={className}>
            <TabsList variant={selectType} >
                {items.map((item: WebTabItemProps, index: number) => (
                    <TabsTrigger value={item.tabId} key={index}>{item.labelContent}</TabsTrigger>
                ))}
            </TabsList>
            {items.map((item: WebTabItemProps, index: number) => (
                <TabsContent value={item.tabId} key={index}>{item.component}</TabsContent>
            ))}
        </Tabs>
    )
}

function Tabs({className, orientation = "horizontal", ...props}: UIComponentProps<typeof TabPrimitive>) {
    return (
        <TabPrimitive
            data-tag="tabs"
            data-orientation={orientation}
            orientation={orientation}
            className={mergeWind(
                "group/tabs flex gap-1 data-[orientation=horizontal]:flex-col",
                className
            )}
            {...props}
        />
    )
}

function TabsTrigger({ className, ...props }: UIComponentProps<typeof TabTriggerPrimitive>) {
  return (
    <TabTriggerPrimitive
      data-tag="tabs-trigger"
      className={mergeWind(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 group-data-[variant=default]/tabs-list:data-[state=active]:shadow-sm group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none dark:text-muted-foreground dark:hover:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:border-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent",
        "data-[state=active]:bg-background data-[state=active]:text-foreground dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 dark:data-[state=active]:text-foreground",
        "after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: UIComponentProps<typeof TabContentPrimitive>) {
  return (
    <TabContentPrimitive
      data-tag="tabs-content"
      className={mergeWind("flex-1 outline-none", className)}
      {...props}
    />
  )
}

const tabsListVariants = makeClassVariance(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-[orientation=horizontal]/tabs:h-9 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line: "gap-1 bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function TabsList({className, variant = "default", ...props}: UIComponentProps<typeof TabListPrimitive> & {variant?: TabSelectionType}) {
    return (
        <TabListPrimitive
            data-tag="tabs-list"
            data-variant={variant}
            className={mergeWind(tabsListVariants({variant}), className)}
            {...props}
        />
    )
}